/* eslint-disable react-hooks/exhaustive-deps */
import { NumberInput } from '@/components/input'
import { useAppSelector } from '@/hooks/useRedux'
import { isEmpty } from 'lodash'
import { useEffect } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { eventSelector } from '../selectors'

type Props = {
  bracketList: any
  currentPlayer: string
  currentClubId: number
}

export const Step2 = ({ bracketList, currentPlayer, currentClubId }: Props) => {
  const { t } = useTranslation()

  const { event } = useAppSelector(eventSelector)

  const { control, getValues, setValue, watch, register } = useFormContext()
  const getOptions = getValues('options')
  const getPrevOptions = getValues('prevOptions')

  const { fields, update } = useFieldArray({
    control,
    name: 'brackets',
  })

  const getPlayerItem = (registrationDetails: any, item: any) => {
    if (registrationDetails?.isParticipant1) {
      return registrationDetails?.player1?.[item]
    }
    return registrationDetails?.player2?.[item]
  }

  const isWithdraw = watch('options') === 'Withdraw'
  const isRefundOnly = watch('options') === 'Refund Only'
  const isWithdrawAndRefund = watch('options') === 'Withdraw and Refund'
  const watchFieldArray = watch('brackets')
  const paidBrackets = bracketList.filter(
    (item: any) => getPlayerItem(item?.registrationDetails, 'paymentStatus') === 'COMPLETE'
  )

  useEffect(() => {
    if (getOptions !== getPrevOptions) {
      setValue('prevOptions', getValues('options'))
      const eventInfo = {
        eventId: event?.leagueId,
        clubId: currentClubId,
        name: event?.leagueName,
        type: 'event',
        minLimit: event?.currencyDetails?.minLimit,
        option: getOptions,
        checked: false,
        currencySymbol: event?.currencyDetails?.currencySymbol,
        refund: '',
        refundAmount: 0,
        registrationDetails: isEmpty(paidBrackets)
          ? bracketList?.[0]?.registrationDetails
          : paidBrackets?.[0]?.registrationDetails,
        processRefund: false,
        memberFee: event?.memberFee,
        nonMemberFee: event?.nonMemberFee,
      }
      update(0, eventInfo)
      bracketList.forEach((item: any, index: any) => {
        const obj = {
          ...item,
          type: 'bracket',
          minLimit: event?.currencyDetails?.minLimit,
          currencySymbol: event?.currencyDetails?.currencySymbol,
          checked: false,
          option: getOptions,
          refund: '',
          refundAmount: 0,
          processRefund: false,
          withdrawPlayer: false,
        }
        update(index + 1, obj)
      })
    }
  }, [getOptions])

  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index],
    }
  })

  const selectedBrackets = controlledFields.filter((item) => item.type === 'bracket' && item?.checked)
  const sum = controlledFields.reduce((accumulator, object) => {
    return accumulator + Number(object?.refund)
  }, 0)

  useEffect(() => {
    setValue('total', sum)
  }, [sum])

  useEffect(() => {
    if (isWithdrawAndRefund) {
      if (selectedBrackets.length === bracketList?.length && selectedBrackets?.length !== 0) {
        setValue('brackets.0.processRefund', true)
        if (getPlayerItem(controlledFields?.[0]?.registrationDetails, 'clubMember')) {
          const memberRefundAmount =
            controlledFields?.[0]?.memberFee - controlledFields?.[0]?.registrationDetails?.eventRefundedAmount
          setValue('brackets.0.refund', memberRefundAmount)
        } else {
          const nonMemberFeeRefundAmount =
            controlledFields?.[0]?.nonMemberFee - controlledFields?.[0]?.registrationDetails?.eventRefundedAmount
          setValue('brackets.0.refund', nonMemberFeeRefundAmount)
        }
      } else {
        setValue('brackets.0.processRefund', false)
        setValue('brackets.0.refund', 0)
      }
    }
  }, [selectedBrackets?.length])

  const status = (memberFee: any, nonMemberFee: any, registrationDetails: any, type: any) => {
    if ((memberFee > 0 || nonMemberFee > 0) && getPlayerItem(registrationDetails, 'paymentStatus') === 'PENDING') {
      return t('event.paymentPending')
    }
    if (type === 'event') {
      if (getPlayerItem(registrationDetails, 'clubMember')) {
        if (memberFee === 0) {
          return t('event.freeEvent')
        }
        if (memberFee === registrationDetails?.eventRefundedAmount && memberFee !== 0) {
          return t('event.fullyRefundedEvent')
        }
        if (memberFee > registrationDetails?.eventRefundedAmount && registrationDetails?.eventRefundedAmount !== 0) {
          return t('event.partiallyRefundedEvent')
        }
      } else {
        if (nonMemberFee === 0) {
          return t('event.freeEvent')
        }
        if (nonMemberFee === registrationDetails?.eventRefundedAmount && nonMemberFee !== 0) {
          return t('event.fullyRefunded')
        }
        if (nonMemberFee > registrationDetails?.eventRefundedAmount && registrationDetails?.eventRefundedAmount !== 0) {
          return t('event.partiallyRefundedEvent')
        }
      }
      return '*Event'
    }
    if (type === 'bracket') {
      if (getPlayerItem(registrationDetails, 'clubMember')) {
        if (Object?.prototype?.hasOwnProperty?.call(registrationDetails, 'player2') && !isRefundOnly) {
          return t('event.splitTeamFirst')
        }
        if (memberFee === 0) {
          return t('event.freeBracket')
        }
        if (memberFee === getPlayerItem(registrationDetails, 'refundAmount') && memberFee !== 0) {
          return t('event.fullyRefunded')
        }
        if (
          memberFee > getPlayerItem(registrationDetails, 'refundAmount') &&
          getPlayerItem(registrationDetails, 'refundAmount') !== 0
        ) {
          return t('event.partiallyRefunded')
        }
      } else {
        if (Object?.prototype?.hasOwnProperty?.call(registrationDetails, 'player2') && !isRefundOnly) {
          return t('event.splitTeamFirst')
        }
        if (nonMemberFee === 0) {
          return t('event.freeBracket')
        }
        if (nonMemberFee === getPlayerItem(registrationDetails, 'refundAmount') && nonMemberFee !== 0) {
          return t('event.fullyRefunded')
        }
        if (
          nonMemberFee > getPlayerItem(registrationDetails, 'refundAmount') &&
          getPlayerItem(registrationDetails, 'refundAmount') !== 0
        ) {
          return t('event.partiallyRefunded')
        }
      }
    }
    return ''
  }
  const withdrawAmount = (memberFee: any, nonMemberFee: any, registrationDetails: any, type: any) => {
    if (type === 'event') {
      if (getPlayerItem(registrationDetails, 'clubMember')) {
        if (registrationDetails?.eventRefundedAmount === 0) {
          return memberFee
        }
        if (memberFee === registrationDetails?.eventRefundedAmount && memberFee !== 0) {
          return 0
        }
        if (memberFee > registrationDetails?.eventRefundedAmount && registrationDetails?.eventRefundedAmount !== 0) {
          return memberFee - registrationDetails?.eventRefundedAmount
        }
      } else {
        if (registrationDetails?.eventRefundedAmount === 0) {
          return nonMemberFee
        }
        if (nonMemberFee === registrationDetails?.eventRefundedAmount && nonMemberFee !== 0) {
          return 0
        }
        if (nonMemberFee > registrationDetails?.eventRefundedAmount && registrationDetails?.eventRefundedAmount !== 0) {
          return nonMemberFee - registrationDetails?.eventRefundedAmount
        }
      }
    }
    if (type === 'bracket') {
      if (getPlayerItem(registrationDetails, 'clubMember')) {
        if (getPlayerItem(registrationDetails, 'refundAmount') === 0) {
          return memberFee
        }
        if (memberFee === getPlayerItem(registrationDetails, 'refundAmount') && memberFee !== 0) {
          return 0
        }
        if (
          memberFee > getPlayerItem(registrationDetails, 'refundAmount') &&
          getPlayerItem(registrationDetails, 'refundAmount') !== 0
        ) {
          return memberFee - getPlayerItem(registrationDetails, 'refundAmount')
        }
      } else {
        if (getPlayerItem(registrationDetails, 'refundAmount') === 0) {
          return nonMemberFee
        }
        if (nonMemberFee === getPlayerItem(registrationDetails, 'refundAmount') && nonMemberFee !== 0) {
          return 0
        }
        if (
          nonMemberFee > getPlayerItem(registrationDetails, 'refundAmount') &&
          getPlayerItem(registrationDetails, 'refundAmount') !== 0
        ) {
          return nonMemberFee - getPlayerItem(registrationDetails, 'refundAmount')
        }
      }
    }
    return ''
  }
  return (
    <>
      <div className="flex flex-col gap-y-6">
        <p className="text-sm text-navy-900 font-semibold">
          {t('event.step2')}: {t('event.registered')} {isRefundOnly ? `${t('event.event')} & ` : ''}
          {t('event.bracketsOf')} {currentPlayer}.
        </p>

        <div className="flex flex-col gap-y-4">
          {controlledFields.map((item, index) => {
            const itemStatus = status(item?.memberFee, item?.nonMemberFee, item?.registrationDetails, item?.type)
            const itemAmount = withdrawAmount(
              item?.memberFee,
              item?.nonMemberFee,
              item?.registrationDetails,
              item?.type
            )
            return (
              <Controller
                key={item.id}
                name={`brackets.${index}.checked`}
                control={control}
                render={({ field }) => {
                  return (
                    <label
                      className={`border rounded p-4 ${
                        item?.option !== 'Refund Only' && item?.type === 'event' ? 'hidden' : 'flex'
                      } items-center justify-between`}
                    >
                      <div className="flex items-center gap-x-4">
                        <input
                          className="w-5 h-5"
                          type="checkbox"
                          disabled={
                            (isRefundOnly && itemAmount === 0) ||
                            ((isWithdraw || isWithdrawAndRefund) && itemStatus === 'Split Team First') ||
                            ((isRefundOnly || isWithdrawAndRefund) &&
                              getPlayerItem(item?.registrationDetails, 'paymentStatus') === 'PENDING' &&
                              (item?.memberFee > 0 || item?.nonMemberFee > 0))
                          }
                          onChange={(e) => {
                            field.onChange(e.target.checked)
                            if (e.target.checked && (itemStatus.startsWith('Partially') || isWithdrawAndRefund)) {
                              setValue(`brackets.${index}.refund`, itemAmount)
                            } else {
                              setValue(`brackets.${index}.refund`, '')
                            }
                            if (isWithdraw) {
                              setValue(`brackets.${index}.withdrawPlayer`, true)
                            }
                            if (isWithdrawAndRefund) {
                              setValue(`brackets.${index}.withdrawPlayer`, true)
                              setValue(`brackets.${index}.processRefund`, itemAmount !== 0)
                            }
                            if (isRefundOnly) {
                              setValue(`brackets.${index}.processRefund`, itemAmount !== 0)
                            }
                          }}
                          checked={field?.value}
                        />
                        <span className="text-base text-navy-900 font-semibold">
                          {item?.name}
                          <span className="text-xs font-medium text-boldOrange">
                            &nbsp;&nbsp;
                            {itemStatus}
                          </span>
                        </span>
                      </div>
                      {!isWithdraw && (
                        <div className="flex items-center gap-x-4">
                          <span className="text-base text-navy-900 text-opacity-70 font-medium">
                            {event?.currencyDetails?.currencySymbol}
                            &nbsp;
                            {itemAmount}
                          </span>
                          {isRefundOnly && (
                            <NumberInput
                              inputClassName="!focus:outline-none !py-2 !px-4 !border !rounded !w-20 !text-sm !font-medium !text-navy-900"
                              min={3}
                              {...register(`brackets.${index}.refund`)}
                              disabled={!watch(`brackets.${index}.checked`) || itemStatus.startsWith('Partially')}
                            />
                          )}
                        </div>
                      )}
                    </label>
                  )
                }}
              />
            )
          })}
        </div>
      </div>

      {!isWithdraw && (
        <div className="flex items-center gap-x-2 mt-10">
          <span className="text-sm font-semibold text-navy-900">{t('event.total')}:</span>
          <NumberInput
            inputClassName="!focus:outline-none !py-2 !px-4 !border !rounded !w-60 !text-sm !font-medium !text-navy-900"
            disabled
          />
        </div>
      )}
    </>
  )
}
